/** Lecture d’un .eml (RFC 822), même forme que l’aperçu Coffre. */

export const EML_MAX_BYTES = 20 * 1024 * 1024;

export type EmlAttachment = { name: string; mime: string; size: number };

export type EmlMessage = {
  subject: string;
  from: string;
  to: string;
  cc: string;
  dateLabel: string;
  html: string;
  text: string;
  attachments: EmlAttachment[];
  remoteImagesHidden: boolean;
};

type HeaderMap = Map<string, string[]>;

type WalkAcc = {
  html: string;
  text: string;
  attachments: EmlAttachment[];
  cid: Map<string, string>;
};

export function parseEml(raw: string): EmlMessage {
  const source = String(raw || "").replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const root = splitHeadBody(source);
  const acc: WalkAcc = { html: "", text: "", attachments: [], cid: new Map() };
  walkPart(root.headers, root.body, 0, acc);

  let html = acc.html;
  if (html && acc.cid.size > 0) {
    html = html.replace(/cid:([^"'\s>]+)/gi, (match, id) => {
      const key = decodeURIComponentSafe(id).replace(/[<>]/g, "");
      return acc.cid.get(key) || acc.cid.get(`<${key}>`) || match;
    });
  }
  const cleaned = html ? neutralizeHtml(html) : { html: "", remoteImagesHidden: false };

  return {
    subject: decodeHeader(headerValue(root.headers, "subject")) || "(Sans objet)",
    from: decodeHeader(headerValue(root.headers, "from")),
    to: decodeHeader(headerValue(root.headers, "to")),
    cc: decodeHeader(headerValue(root.headers, "cc")),
    dateLabel: formatMailDate(headerValue(root.headers, "date")),
    html: cleaned.html,
    text: acc.text,
    attachments: acc.attachments,
    remoteImagesHidden: cleaned.remoteImagesHidden,
  };
}

function walkPart(headers: HeaderMap, body: string, depth: number, acc: WalkAcc): void {
  if (depth > 8) return;
  const ctype = headerValue(headers, "content-type") || "text/plain";
  const mime = ctype.split(";")[0].trim().toLowerCase() || "text/plain";
  const disposition = headerValue(headers, "content-disposition").toLowerCase();
  if (mime.startsWith("multipart/")) {
    const boundary = headerParam(ctype, "boundary");
    if (!boundary) return;
    for (const part of splitMultipart(body, boundary)) {
      const sub = splitHeadBody(part);
      walkPart(sub.headers, sub.body, depth + 1, acc);
    }
    return;
  }

  const cte = headerValue(headers, "content-transfer-encoding").toLowerCase() || "7bit";
  const filename = filenameFrom(headers);
  const cid = headerValue(headers, "content-id").replace(/[<>]/g, "").trim();
  const attached = disposition.includes("attachment") || (filename && mime !== "text/html" && mime !== "text/plain");

  if (!attached && mime === "text/html" && !acc.html) {
    acc.html = decodeText(body, cte, headerParam(ctype, "charset"));
    return;
  }
  if (!attached && mime === "text/plain" && !acc.text) {
    acc.text = decodeText(body, cte, headerParam(ctype, "charset"));
    return;
  }
  if (mime.startsWith("image/") && cid && !disposition.includes("attachment")) {
    const bytes = decodeBinary(body, cte);
    if (bytes && bytes.length > 0 && bytes.length <= 1_500_000) {
      acc.cid.set(cid, `data:${mime};base64,${bytesToBase64(bytes)}`);
    }
    return;
  }
  if (attached || filename || (!mime.startsWith("text/") && mime !== "multipart/mixed")) {
    if (mime === "text/plain" || mime === "text/html") {
      if (!filename && !disposition.includes("attachment")) return;
    }
    acc.attachments.push({
      name: filename || "Pièce jointe",
      mime,
      size: estimatedSize(body, cte),
    });
  }
}

function splitHeadBody(source: string): { headers: HeaderMap; body: string } {
  const idx = source.indexOf("\n\n");
  const headerBlock = idx === -1 ? source : source.slice(0, idx);
  const body = idx === -1 ? "" : source.slice(idx + 2);
  return { headers: parseHeaders(headerBlock), body };
}

function parseHeaders(block: string): HeaderMap {
  const map: HeaderMap = new Map();
  const unfolded = block.replace(/\n[ \t]+/g, " ");
  for (const line of unfolded.split("\n")) {
    const colon = line.indexOf(":");
    if (colon <= 0) continue;
    const key = line.slice(0, colon).trim().toLowerCase();
    const value = line.slice(colon + 1).trim();
    const list = map.get(key);
    if (list) list.push(value);
    else map.set(key, [value]);
  }
  return map;
}

function headerValue(headers: HeaderMap, name: string): string {
  return (headers.get(name) || []).join(", ");
}

function headerParam(header: string, name: string): string {
  const match = new RegExp(`${name}\\s*=\\s*(?:"([^"]*)"|([^;\\s]+))`, "i").exec(header);
  return (match?.[1] ?? match?.[2] ?? "").trim();
}

function filenameFrom(headers: HeaderMap): string {
  const disposition = headerValue(headers, "content-disposition");
  const ctype = headerValue(headers, "content-type");
  const star = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(disposition);
  if (star?.[2]) return decodeURIComponentSafe(star[2].trim().replace(/^"|"$/g, ""));
  const named =
    /filename\s*=\s*"([^"]+)"/i.exec(disposition) ||
    /filename\s*=\s*([^;\s]+)/i.exec(disposition) ||
    /name\s*=\s*"([^"]+)"/i.exec(ctype) ||
    /name\s*=\s*([^;\s]+)/i.exec(ctype);
  if (!named?.[1]) return "";
  return decodeHeader(named[1].replace(/^"|"$/g, ""));
}

function splitMultipart(body: string, boundary: string): string[] {
  const marker = `--${boundary}`;
  const parts: string[] = [];
  const chunks = body.split(marker);
  for (let i = 1; i < chunks.length; i += 1) {
    let chunk = chunks[i];
    if (chunk.startsWith("--")) break;
    if (chunk.startsWith("\n")) chunk = chunk.slice(1);
    if (chunk.endsWith("\n")) chunk = chunk.slice(0, -1);
    if (chunk) parts.push(chunk);
  }
  return parts;
}

function decodeHeader(value: string): string {
  const joined = String(value || "").replace(/\?=\s+=\?/g, "?==?");
  return joined
    .replace(/=\?([^?]+)\?([bBqQ])\?([^?]*)\?=/g, (_, charset: string, enc: string, data: string) => {
      try {
        const bytes = enc.toLowerCase() === "b" ? base64ToBytes(data) : qpToBytes(data.replace(/_/g, " "));
        return decodeBytes(bytes, charset);
      } catch {
        return data;
      }
    })
    .replace(/\s+/g, " ")
    .trim();
}

function decodeText(body: string, cte: string, charset: string): string {
  if (cte.includes("base64") || cte.includes("quoted-printable")) {
    const bytes = decodeBinary(body, cte);
    return bytes ? decodeBytes(bytes, charset || "utf-8") : "";
  }
  return body.replace(/\n+$/g, "");
}

function decodeBinary(body: string, cte: string): Uint8Array | null {
  if (cte.includes("base64")) return base64ToBytes(body);
  if (cte.includes("quoted-printable")) return qpToBytes(body);
  return null;
}

function estimatedSize(body: string, cte: string): number {
  if (cte.includes("base64")) return Math.floor((body.replace(/\s/g, "").length * 3) / 4);
  return body.length;
}

function base64ToBytes(input: string): Uint8Array {
  const clean = String(input || "").replace(/\s/g, "");
  if (!clean) return new Uint8Array();
  const binary = atob(clean);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function qpToBytes(input: string): Uint8Array {
  const cleaned = String(input || "").replace(/=\n/g, "");
  const out: number[] = [];
  for (let i = 0; i < cleaned.length; i += 1) {
    if (cleaned[i] === "=" && /^[0-9A-Fa-f]{2}$/.test(cleaned.slice(i + 1, i + 3))) {
      out.push(parseInt(cleaned.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      out.push(cleaned.charCodeAt(i) & 0xff);
    }
  }
  return new Uint8Array(out);
}

function decodeBytes(bytes: Uint8Array, charset: string): string {
  const label = String(charset || "utf-8").trim().toLowerCase() || "utf-8";
  try {
    return new TextDecoder(label).decode(bytes);
  } catch {
    return new TextDecoder("utf-8").decode(bytes);
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(binary);
}

function decodeURIComponentSafe(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function formatMailDate(raw: string): string {
  const value = String(raw || "").trim();
  if (!value) return "";
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return value;
  return new Date(parsed).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
}

function neutralizeHtml(html: string): { html: string; remoteImagesHidden: boolean } {
  let remoteImagesHidden = false;
  let next = String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");
  next = next.replace(/\s(src|srcset)\s*=\s*(?:"https?:[^"]*"|'https?:[^']*')/gi, (_match, attr: string) => {
    remoteImagesHidden = true;
    return ` ${attr}=""`;
  });
  return { html: next, remoteImagesHidden };
}
